/**
 * Pype Loader type definitions.
 *
 * All public types, enums, interfaces, and the {@link PypeLoaderError} class
 * used across the Loader surface area.
 *
 * @module
 */

// Type-only, so it is erased at build and adds nothing to the Loader bundle — the reason this one
// import does not breach the Loader's deliberately small dependency surface.
import type { PypeLanguage } from '@pypestream/translations/types';

// ---------------------------------------------------------------------------
// LoaderStatus
// ---------------------------------------------------------------------------

/**
 * All possible Loader lifecycle statuses.
 *
 * Use `loader.status` or `loader.getState().status` to read the current value.
 */
export enum LoaderStatus {
  /** Loader created but boot() not yet called. */
  Idle = 'idle',
  /** Loading configuration and mounting the widget. */
  Initializing = 'initializing',
  /** Chat is active — widget mounted and communicating. */
  ChatActive = 'chat_active',
  /** Chat has ended — widget unmounted, session cleared. */
  ChatEnded = 'chat_ended',
  /** An error occurred. Call reset() to recover. */
  Error = 'error',
}

// ---------------------------------------------------------------------------
// LoaderErrorCode + PypeLoaderError
// ---------------------------------------------------------------------------

/**
 * Machine-readable error codes for all Loader errors.
 *
 * Every {@link PypeLoaderError} includes one of these codes
 * so consumers can programmatically react to specific failures.
 */
export enum LoaderErrorCode {
  /** boot() called when Loader is already booted or booting. */
  ALREADY_BOOTED = 'ALREADY_BOOTED',
  /** Config source returned invalid or missing data. */
  INVALID_CONFIG = 'INVALID_CONFIG',
  /** Method called in a state where it's not allowed. */
  INVALID_STATE = 'INVALID_STATE',
  /** sendMetadata() called with a non-object/array, too-deep, or too-large payload. */
  INVALID_METADATA = 'INVALID_METADATA',
  /** Internal state transition was illegal. */
  INVALID_TRANSITION = 'INVALID_TRANSITION',
  /** DOM mounting or web component registration failed. */
  MOUNT_FAILED = 'MOUNT_FAILED',
  /**
   * Window render target (`target: 'window'`) could not open its popup —
   * the browser blocked it (boot must run inside a user gesture) or the
   * runtime URL could not be resolved (bundler/npm consumers must pass
   * `window.bootstrapUrl`).
   */
  WINDOW_BLOCKED = 'WINDOW_BLOCKED',
  /** appId is missing or empty. */
  MISSING_APP_ID = 'MISSING_APP_ID',
  /** An unknown/unexpected error occurred. */
  UNKNOWN = 'UNKNOWN',
}

/**
 * Structured error class for all Loader errors.
 *
 * Every error has a machine-readable {@link code} and a human-readable message.
 * Use `code` for programmatic handling and `message` for logging / display.
 *
 * @example
 * ```ts
 * try {
 *   await loader.boot();
 * } catch (err) {
 *   if (err instanceof PypeLoaderError) {
 *     console.error(`[${err.code}] ${err.message}`);
 *   }
 * }
 * ```
 */
export class PypeLoaderError extends Error {
  /** Machine-readable error code. */
  readonly code: LoaderErrorCode;

  /** Loader status at the time the error occurred. */
  readonly loaderStatus: LoaderStatus;

  /** Original error that caused this one, if any. */
  override readonly cause?: Error;

  constructor(
    code: LoaderErrorCode,
    message: string,
    options?: { cause?: Error; loaderStatus?: LoaderStatus }
  ) {
    super(message, options?.cause ? { cause: options.cause } : undefined);
    this.name = 'PypeLoaderError';
    this.code = code;
    this.loaderStatus = options?.loaderStatus ?? LoaderStatus.Error;
    this.cause = options?.cause;
  }
}

// ---------------------------------------------------------------------------
// PypeError (public structured payload)
// ---------------------------------------------------------------------------

/**
 * Error codes for failures that occur inside the widget runtime
 * (`<ps-pype>` boot, render, lifecycle, XState actor, dynamic modality load).
 *
 * Distinct from {@link LoaderErrorCode}, which covers failures in the host-side
 * Loader before/around `<ps-pype>` mount.
 */
export type WidgetErrorCode =
  /** Lit/render or lifecycle exception inside the widget shadow tree. */
  | 'RENDER_FAILED'
  /** XState interpreter / actor crashed. */
  | 'RUNTIME_CRASHED'
  /** Dynamic `import()` of a modality (floating/fullpage) failed. */
  | 'MODALITY_LOAD_FAILED'
  /** Unknown/unexpected widget-zone error. */
  | 'UNKNOWN_WIDGET_ERROR';

/**
 * Error codes for failures that occur inside a single message component.
 *
 * Component errors are isolated per-message — they never transition the
 * widget machine into an unavailable state. One bad component never
 * kills the chat.
 */
export type EmbedErrorCode =
  | 'COMPONENT_RENDER_FAILED'
  | 'COMPONENT_UNKNOWN_TYPE'
  | 'COMPONENT_HANDLER_FAILED';

/**
 * Public structured error payload delivered to {@link CreatePypeLoaderOptions.onError}
 * and the Loader `'error'` event bus.
 *
 * Discriminated union on `zone` — narrow on the zone field to access the
 * zone-specific fields.
 *
 * @example
 * ```ts
 * createPypeLoader({
 *   appId: 'app_123',
 *   onError(err) {
 *     if (err.zone === 'embed') {
 *       console.warn(`embed ${err.embedType} in message ${err.messageId} failed`);
 *     }
 *   },
 * });
 * ```
 */
export type PypeError =
  | {
      /** Failure in the host-side Loader — before/around `<ps-pype>` mount. */
      zone: 'loader';
      /** Machine-readable code. */
      code: LoaderErrorCode;
      /** Human-readable message. */
      message: string;
      /** Original thrown value, if any. Loader code is trusted; passed through unsanitized. */
      originalError?: unknown;
      /** Unix epoch milliseconds at the time the error was captured. */
      timestamp: number;
    }
  | {
      /** Failure inside the widget runtime tree (`<ps-pype>` and descendants). */
      zone: 'widget';
      code: WidgetErrorCode;
      message: string;
      /** Original thrown value, if any. Widget code is trusted; passed through unsanitized. */
      originalError?: unknown;
      timestamp: number;
    }
  | {
      /** Failure inside a single custom-asset instance. Isolated — does not affect widget state. */
      zone: 'embed';
      code: EmbedErrorCode;
      componentType: string;
      messageId: string;
      instanceId: string;
      message: string;
      /** Sanitized cause — embed code may be untrusted, so we expose only `name`/`message`. */
      cause?: { name: string; message: string };
      timestamp: number;
    };

// ---------------------------------------------------------------------------
// LoaderCommand
// ---------------------------------------------------------------------------

/**
 * Commands that can be issued to the Loader.
 *
 * Used with {@link PypeLoader.can} to check whether a command
 * is allowed in the current state.
 */
export type LoaderCommand =
  | 'boot'
  | 'shutdown'
  | 'endChat'
  | 'reset'
  | 'restart'
  | 'update'
  | 'expand'
  | 'minimize'
  | 'toggle'
  | 'unmount'
  | 'applyStyles'
  | 'applyBranding'
  | 'sendMetadata'
  | 'registerForEvents';

/**
 * A host-page metadata payload. Sent via {@link PypeLoader.sendMetadata} (or
 * the deprecated {@link PypeLoader.registerForEvents} bridge) and surfaced to
 * the bot on its next request as `metadata.events`. Must be a JSON object
 * or array — not a primitive.
 */
export type MetadataPayload = Record<string, unknown> | unknown[];

// ---------------------------------------------------------------------------
// User data + passthrough
// ---------------------------------------------------------------------------

/**
 * Host-supplied user identity, forwarded to the backend at chat-session
 * creation and surfaced to the solution. All fields optional. Field names are
 * camelCase here and mapped to the backend's snake_case wire names internally.
 *
 * Set once at {@link createPypeLoader} — applied to the initial session only
 * (runtime `update()` does not re-create the session).
 */
export interface PypeUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  screenName?: string;
  /**
   * Opaque auth/identity token forwarded to the backend at session creation.
   * Treated as sensitive — redacted from {@link PypeLoader.getConfig} and the
   * `update` event payload.
   */
  token?: string;
}

/**
 * Opaque passthrough payload forwarded verbatim to the backend session body
 * (read by the solution via `bot_or_context`). Sent exactly as provided — never
 * coerced. Accepts a string, an object, or the `[{ name, value }]` array form.
 */
export type PypePassthrough =
  | string
  | Record<string, unknown>
  | Array<{ name: string; value?: string }>;

/**
 * Host-supplied first message(s), shown while the widget establishes its
 * backend/socket connection — so the user sees content immediately instead of
 * a bare loading placeholder. Legacy parity for `initialMessages`.
 */
export interface PypeInitialMessages {
  /** Welcome text rendered as a bot bubble during connection. */
  welcomeMessage?: string;
  /** Show a "thinking" indicator after the welcome bubble. @defaultValue `false` */
  showThinkingBubble?: boolean;
  /**
   * Keep the welcome bubble visible after real messages arrive (vs. replacing
   * it). @defaultValue `false`
   */
  persistent?: boolean;
}

// ---------------------------------------------------------------------------
// Window render target
// ---------------------------------------------------------------------------

/**
 * Popup tuning for the `window` render target (`target: 'window'`).
 *
 * All fields optional — the Loader picks sensible defaults (size by modality;
 * the browser places the window). The
 * popup is same-origin (`about:blank`), so it shares the host page's
 * `localStorage` (session continuity) and can import the Pype runtime from the
 * host's own origin.
 */
export interface PypeWindowOptions {
  /**
   * `window.open` target name. A stable name lets a re-created Loader (e.g.
   * after the opener page reloads, or a launcher that re-creates the Loader per
   * click) reuse the SAME popup instead of spawning a second one. If that popup
   * already has a live widget, the Loader ADOPTS it — re-acquiring the running
   * conversation without restarting it; only a popup with no live runtime gets a
   * fresh bootstrap. (Session state also survives in the shared same-origin
   * `localStorage`.)
   * @defaultValue `` `pypestream-${appId}` ``
   */
  name?: string;
  /**
   * Popup width in CSS pixels. Defaults are modality-aware: `420` for
   * `floating` (a compact chat window), `960` for `fullpage` (a page layout).
   */
  width?: number;
  /**
   * Popup height in CSS pixels. Defaults are modality-aware: `640` for
   * `floating`, `720` for `fullpage`.
   */
  height?: number;
  /**
   * Raw `window.open` feature string. When set it fully replaces the
   * Loader-computed features (width/height/centering are then your
   * responsibility). Most integrations should use `width`/`height` instead.
   */
  features?: string;
  /**
   * Absolute URL to the Pype runtime bundle the popup should import.
   *
   * **You almost never set this.** On the CDN build it is auto-resolved from
   * the loader's own origin (the same mechanism that lets inline/iframe embeds
   * work without a URL). Only bundler/npm consumers — where the CDN global is
   * unset — must provide it, pointing at the deployed `@pypestream/pype`
   * runtime. Leave it blank for CDN embeds.
   */
  bootstrapUrl?: string;
}

// ---------------------------------------------------------------------------
// AppConfig
// ---------------------------------------------------------------------------

/**
 * Application configuration passed to the Pype widget.
 *
 * Populated from {@link CreatePypeLoaderOptions} and validated during Loader construction.
 */
/**
 * Floating panel frame size — mirrors the widget's `shell.size`. Designer-owned
 * and boot-immutable; applies only to the floating default-launcher panel.
 */
export interface ShellSizeConfig {
  width: number;
  height: number;
  lockRatio: boolean;
  position?: string;
}

export interface AppConfig {
  /** Application identifier used for session creation and cookie storage. */
  appId: string | null;
  /** Language code for the chat interface (e.g. `'en'`, `'es'`). */
  language: PypeLanguage | (string & Record<never, never>);
  /**
   * Color theme for the widget.
   *
   * **Precedence:** the saved design wins — if a design is saved for the
   * `appId`, its theme overrides this value. This is the fallback used before
   * the design loads, or when no design is saved. Runtime theme changes are
   * delivered by the saved design (edited in the Pype designer), not by the
   * host at runtime.
   */
  theme: string | null;
  /**
   * Text displayed on the launcher button.
   *
   * **Precedence:** the saved design wins (designer-owned branding). This is
   * the fallback used before the design loads or when none is saved.
   */
  launcherButtonText: string | null;
  /** Width of the launcher button (CSS value). */
  launcherButtonSize: string | null;
  /** Position of the launcher on screen (e.g. `'bottom-right'`). */
  launcherPosition: string | null;
  /** Visual variant of the launcher button. */
  launcherVariant: string | null;
  /**
   * URL of the customer's logo, shown in the widget header.
   *
   * **Precedence:** the saved design wins (designer-owned branding); this is
   * the fallback used before the design loads or when none is saved.
   */
  customerLogoUrl: string | null;
  /**
   * Customer display name, shown in the widget header.
   *
   * **Precedence:** host-only. The saved design carries no customer name, so
   * this is the only source — a designer overrides the displayed brand name
   * with the design's display name, which wins over this.
   */
  customerName: string | null;
  /**
   * Host-supplied user identity, forwarded to chat-session creation and
   * surfaced to the solution. Applied to the initial session only.
   */
  user?: PypeUserData;
  /**
   * Opaque passthrough forwarded verbatim to the backend session body. Applied
   * to the initial session only.
   */
  passthrough?: PypePassthrough;
  /**
   * Host-supplied first message shown while the widget connects. Legacy parity
   * for `initialMessages`.
   */
  initialMessages?: PypeInitialMessages;
  /**
   * Show the built-in loading placeholder while connecting. Set `false` to let
   * the host page own the loading UI (typically paired with `initialMessages`).
   * Legacy parity for `withInlineAppLoader`. @defaultValue `true`
   */
  showInlineLoader?: boolean;
  /**
   * Disable the floating messenger's slide-in/out animation. Legacy parity for
   * `disableWidgetSlideInOutAnimation`. @defaultValue `false`
   */
  disableSlideAnimation?: boolean;
  /** Enable verbose console logging. Legacy parity for `debugMode`. @defaultValue `false` */
  debug?: boolean;
  /**
   * Whether to render the messenger inside an iframe for CSS/DOM isolation.
   *
   * **Precedence:** config-only — the saved design never sets this. iframe
   * isolation is an integration-mechanics choice only the host page can make,
   * so the embed always owns it. @defaultValue `false`
   */
  useIframe?: boolean;
  /**
   * Rendering modality — same bot flow, different visual presentation.
   * - `'floating'` (default): Launcher FAB + popup messenger.
   * - `'fullpage'`: Messenger fills the container, no launcher.
   *
   * **Precedence:** your value overrides the saved design's modality. Omit it
   * to inherit the modality saved for the `appId` (which itself falls back to
   * `'floating'`). This lets the same bot float on one page and run fullpage on
   * another without re-saving the design.
   * @defaultValue `'floating'`
   */
  modality?: 'floating' | 'fullpage';
  /**
   * Floating panel frame size. Boot-immutable and designer-owned — normally
   * supplied by the saved design; pass it here only for the designer preview
   * (which skips the design fetch). Ignored in `fullpage` / `launcher:'none'`.
   */
  size?: ShellSizeConfig;
  /**
   * What the widget's fixed-position chrome anchors to, and what its
   * viewport-relative sizing resolves against.
   *
   * - `'viewport'` (default): the browser window. The launcher docks to the
   *   page corner, the panel is sized off the viewport, and it goes full-screen
   *   below the mobile breakpoint. This is a customer embed.
   * - `'container'`: the Loader's `container`. Everything — launcher, panel,
   *   tray, expanded overlay — stays inside that box, and the breakpoint is
   *   decided by the container's width, not the window's. For rendering the
   *   widget inside a bounded panel of a larger app.
   *
   * `'container'` requires the container to have a DEFINITE height: it makes the
   * widget a size-query container, so a percentage height against an
   * auto-height ancestor resolves to zero, not to content height.
   *
   * **Precedence:** config-only and boot-immutable — the saved design never
   * sets this. Where the widget is being rendered is a property of the host,
   * not of the customer's design. Same family as `launcher` / `useIframe` /
   * `target`.
   *
   * @internal Pro Studio / internal tooling only — not part of the public
   * embed surface.
   * @defaultValue `'viewport'`
   */
  anchorTo?: 'viewport' | 'container';
  /**
   * Floating-modality launcher style.
   * - `'default'` (default): Pype renders its built-in `<ps-pype-launcher>`.
   * - `'none'`: headless — the host page mounts its own launcher and drives
   *   the messenger via `loader.expand()` / `minimize()` / `toggle()`. The
   *   messenger fills the Loader's container instead of being corner-anchored.
   *
   * **Precedence:** config-only — the saved design never sets this. Whether
   * the host ships its own launcher is an integration choice the designer
   * can't know, so the embed always owns it.
   *
   * Ignored in `modality: 'fullpage'`. Setting `'default'` with fullpage
   * triggers a dev warning and is overridden to `'none'`.
   * @defaultValue `'default'`
   */
  launcher?: 'default' | 'none';
  /**
   * Render target — WHERE the widget renders, orthogonal to {@link modality}
   * (HOW it looks). Any modality can render in either target.
   *
   * - `'inline'` (default): the widget mounts into the host page (the
   *   `container`, or `document.body`).
   * - `'window'`: the Loader opens a same-origin popup window and the widget
   *   runs inside it. No iframe — the popup imports the Pype runtime in its own
   *   realm and the Loader drives it through a live element reference, so every
   *   method/getter/event behaves exactly as inline. The widget renders
   *   **headless** (no launcher — `launcher` is forced to `'none'`): a popup
   *   has no host page to anchor a FAB to, so the messenger (floating) or the
   *   page (fullpage) fills the popup directly. See {@link PypeWindowOptions}.
   *
   * **Precedence:** config-only — never sourced from the saved design. Whether
   * to pop out is an integration choice the designer can't know.
   * @defaultValue `'inline'`
   */
  target?: 'inline' | 'window';
  /** Popup tuning for `target: 'window'`. Ignored for `target: 'inline'`. */
  window?: PypeWindowOptions;
  /**
   * When `true`, the widget uses `engagementApiUrl` for session and socket
   * instead of the build-time engagement API constant. For designer/preview
   * hosts that know the environment at runtime.
   *
   * Note: this does NOT control the persisted-design fetch — use
   * {@link skipDesignFetch} for that.
   *
   * @internal Pro Studio / internal tooling only — not part of the public
   * embed surface. Customer sites talk only to the backend baked into the
   * deployment they load the script from.
   * @defaultValue `false`
   */
  preview?: boolean;
  /**
   * When `true`, skip the persisted widget-design fetch; the caller supplies
   * styling via {@link PypeLoader.applyStyles}. When `false` (default) the
   * widget fetches and applies the design saved for the `appId` automatically.
   *
   * This is the master switch behind the per-field precedence: with the fetch
   * ON (default), the "saved design wins" fields (`theme`, branding) take their
   * value from the design and `modality` falls back to it. With the fetch
   * SKIPPED, there is no saved design to win, so every field comes purely from
   * caller config. The config-only fields (`launcher`, `useIframe`) behave the
   * same either way — they are never sourced from the design.
   *
   * @internal Pro Studio / internal test harnesses only — not part of the
   * public embed surface. Customer sites always let the widget fetch and
   * apply its saved design; the designer and scenario test pages set this so
   * caller-supplied config wins instead.
   * @defaultValue `false`
   */
  skipDesignFetch?: boolean;
  /**
   * Engagement API base URL override. Used when `preview` is `true` (session
   * and socket) and, regardless of `preview`, as the design-fetch backend
   * when the fetch is not skipped.
   *
   * @internal Pro Studio / internal tooling only — not part of the public
   * embed surface. The backend is baked into each deployment at build time.
   * Example: `'https://engagement-api-sandbox.main.pypestream.dev'`
   */
  engagementApiUrl?: string | null;
}

// ---------------------------------------------------------------------------
// RuntimeUpdatableConfig
// ---------------------------------------------------------------------------

/**
 * The subset of {@link AppConfig} that {@link PypeLoader.update} accepts at
 * runtime. Deliberately narrow:
 *
 * - **Visual styling is NOT here** — `theme`, tokens, CSS parts, and raw CSS
 *   are owned by the saved design (edited in the Pype designer) and fetched at
 *   boot. Putting `theme` in both would be two ways to do one thing.
 * - **Session/boot-only fields are NOT here** — `modality`, `launcher`,
 *   `useIframe`, `anchorTo`, `user`, `passthrough`, `initialMessages`, etc. are
 *   fixed for the life of a session; change them at {@link createPypeLoader}
 *   (they take effect on the next boot), not via `update()`.
 *
 * What remains is non-visual, conceptually re-settable config (language and
 * branding text).
 */
export type RuntimeUpdatableConfig = Partial<
  Pick<
    AppConfig,
    | 'language'
    | 'customerName'
    | 'customerLogoUrl'
    | 'launcherButtonText'
    | 'launcherButtonSize'
    | 'launcherPosition'
    | 'launcherVariant'
  >
>;

// ---------------------------------------------------------------------------
// StyleOverrides
// ---------------------------------------------------------------------------

/**
 * Structured style overrides applied to the Pype widget via {@link PypeLoader.applyStyles}.
 *
 * Tokens and part overrides are compiled into scoped CSS by `<ps-pype>`.
 * `rawCss` is sanitized before injection to prevent XSS.
 *
 * @internal Designer-preview only — the type accepted by the internal
 * {@link PypeLoader.applyStyles}. Not part of the public embed surface.
 *
 * @example
 * ```ts
 * loader.applyStyles({
 *   theme: 'dark',
 *   tokens: { '--color-primary': '#ff0044' },
 *   partOverrides: [{
 *     component: 'ps-pype-chat-bubble',
 *     part: 'bubble-root',
 *     declarations: { 'border-radius': '0' },
 *   }],
 * });
 * ```
 */
export interface StyleOverrides {
  /** DaisyUI theme name (e.g. `'light'`, `'dark'`). */
  theme?: string;
  /** CSS custom property overrides (variable name → value). */
  tokens?: Record<string, string>;
  /** Per-component `::part()` CSS overrides. */
  partOverrides?: Array<{
    /** Web component tag name (e.g. `'ps-pype-chat-bubble'`). */
    component: string;
    /** CSS part name (e.g. `'bubble-root'`). */
    part: string;
    /** CSS property → value pairs. */
    declarations: Record<string, string>;
  }>;
  /** Raw CSS escape hatch — appended verbatim after sanitization. */
  rawCss?: string;
}

/**
 * Branding overrides applied to the mounted widget via the internal
 * {@link PypeLoader.applyBranding}. A FULL snapshot — the widget replaces its
 * branding wholesale (not a patch), so omitting a field clears it.
 *
 * @internal Designer-preview only, symmetric to {@link StyleOverrides}. Mirrors
 * the widget's `BrandingSection` locally (the loader must not import widget
 * internals — see the hard boundary). If that upstream shape changes, update
 * both ends.
 */
export interface BrandingOverrides {
  customerLogoUrl: string;
  displayName?: string;
  bannerUrl?: string;
  brandMomentUrl?: string;
  launcherName?: string;
}

// ---------------------------------------------------------------------------
// Session types
// ---------------------------------------------------------------------------

/**
 * Session info exposed in public Loader state when chat is active or ended.
 * Omits the access token since consumers should not need it directly.
 *
 * Populated asynchronously from `<ps-pype>`'s `pype-session-created` event.
 */
export interface SessionInfo {
  chatId: string;
  pypeId: string;
  streamId: string;
  userId: string;
}

// ---------------------------------------------------------------------------
// LoaderState
// ---------------------------------------------------------------------------

/**
 * Loader state snapshot returned by {@link PypeLoader.getState}.
 *
 * This is a discriminated union on `status` — each status variant
 * carries only the context relevant to that lifecycle phase.
 */
export type LoaderState =
  | { status: 'idle' }
  | { status: 'initializing' }
  | {
      status: 'chat_active';
      conversationId: string;
      session: SessionInfo;
    }
  | { status: 'chat_ended'; session: SessionInfo }
  | { status: 'error'; error: PypeLoaderError };

// ---------------------------------------------------------------------------
// LoaderEvents
// ---------------------------------------------------------------------------

/**
 * Event payloads emitted by the Loader.
 *
 * Subscribe via `loader.on('event-name', callback)`.
 *
 * @example
 * ```ts
 * loader.on('chat:start', ({ conversationId }) => {
 *   console.log('Chat started:', conversationId);
 * });
 *
 * loader.on('state:change', (state) => {
 *   console.log('New status:', state.status);
 * });
 * ```
 */
export interface LoaderEvents {
  /** Fired when a chat session is successfully created. */
  'chat:start': { conversationId: string };
  /**
   * Fired when the conversation ends. Three triggers: a `shutdown()` (which
   * also clears the session + unmounts), a host `endChat()`, or a natural end
   * (agent/server end, or an ended snapshot). For `endChat()` / natural end the
   * widget stays mounted on the chat-ended screen and the Loader moves to
   * `chat_ended` — `restart()` starts a fresh conversation from there.
   */
  'chat:end': void;
  /**
   * Fired once when the user (or a host `endChat()`) asks to end, BEFORE the
   * backend has answered. The answer may be "no" — a configured survey or an
   * exit-node route continues the conversation — so this is not a promise the
   * chat is ending. Use it to render a pending affordance; wait for `chat:end`
   * for the fact.
   *
   * Emitted on the rising edge and then latched: `endRequested` is derived from
   * a state region the widget deliberately does not persist, so a reconnect
   * mid-request makes it dip false and re-derive true. The latch is what stops
   * that becoming a second event for one user action.
   */
  'chat:end-requested': void;
  /**
   * Fired when the conversation is restarted via {@link PypeLoader.restart} —
   * the previous conversation is torn down and a fresh session is created.
   * Legacy parity for `onChatRestart`. Note: an in-widget "Start New
   * Conversation" click does NOT currently cross the Loader boundary, so this
   * fires only for SDK-initiated `restart()` calls.
   */
  'chat:restart': void;
  /**
   * Fired once per session when the widget has established all required
   * backend/socket connections and is ready to receive messages (the runtime
   * reached its `ready` state). Legacy parity for `onSetupComplete`. Distinct
   * from `messenger:ready`, which fires at shell mount, before the socket.
   */
  'setup:complete': void;
  /**
   * Fired just before the first bot/agent message renders. Legacy parity for
   * `onFirstMessage`. Useful for hosts that hold a loading state until Pype
   * shows real content.
   */
  'message:first': void;
  /** Fired on every status/state transition. */
  'state:change': LoaderState;
  /** Fired when `loader.update()` is called with partial settings. */
  update: RuntimeUpdatableConfig;
  /**
   * Fired when `loader.applyStyles()` is called with style overrides.
   * @internal Designer-preview only — not emitted for live/customer embeds.
   */
  applyStyles: StyleOverrides;
  /**
   * Fired when `loader.applyBranding()` is called with branding overrides.
   * @internal Designer-preview only — not emitted for live/customer embeds.
   */
  applyBranding: BrandingOverrides;
  /** Fired when `<ps-pype>` is mounted into the DOM. */
  mount: void;
  /** Fired when `<ps-pype>` is unmounted from the DOM. */
  unmount: void;
  /**
   * Fired when the launcher web component is created and ready.
   * NOT fired in headless mode (`launcher: 'none'`) — there is no built-in
   * launcher to be ready. Host-page code should subscribe to `state:change`
   * for readiness in headless integrations.
   */
  'launcher:ready': void;
  /** Fired when the messenger web component is created and ready. */
  'messenger:ready': void;
  /** Fired when the messenger panel opens. */
  'messenger:open': void;
  /** Fired when the messenger panel closes. */
  'messenger:close': void;
  /** Fired when busy/error state changes (drive the custom launcher button). */
  'launcher-state:change': { busy: boolean; hasError: boolean };
  /**
   * Fired when the `window` render target's popup is opened (right after
   * `window.open` succeeds, before the widget finishes mounting inside it).
   * Only fires for `target: 'window'`.
   */
  'window:open': void;
  /**
   * Fired when the `window` render target's popup closes — whether the user
   * closed it or the host called `shutdown()`/`reset()`/`unmount()`. When the
   * user closes it mid-chat, the Loader also emits `chat:end` and moves to
   * `chat_ended`; the session is kept so a subsequent `boot()` can restore it.
   * Only fires for `target: 'window'`.
   */
  'window:close': void;
  /** Fired on any Loader error (in addition to `state:change`). */
  error: PypeLoaderError;
}

// ---------------------------------------------------------------------------
// CreatePypeLoaderOptions + BootOptions
// ---------------------------------------------------------------------------

/**
 * Options for creating a new Loader instance via {@link createPypeLoader}.
 *
 * All config fields from {@link AppConfig} are available directly on this
 * interface. Only `appId` is required — everything else has sensible
 * defaults (`language: 'en'`, `modality: 'floating'`).
 *
 * Pass resolved config values directly — the Loader does not read URL params
 * or fetch config from external sources. If the host website needs to
 * derive config from query strings, an API, or any other source, it should
 * resolve those values itself and pass the results here.
 *
 * @example
 * ```ts
 * const loader = createPypeLoader({
 *   appId: 'app_123',
 *   language: 'en',
 * });
 *
 * await loader.boot();
 * ```
 */
export interface CreatePypeLoaderOptions {
  /** Application identifier used for session persistence in localStorage. */
  appId: string;
  /**
   * Container element to mount the Pype widget into.
   * Defaults to `document.body` if omitted.
   */
  container?: HTMLElement;

  /**
   * Optional callback invoked on every Loader, widget, or embed error.
   *
   * Fires in addition to the existing `'error'` event on the Loader event bus —
   * both sinks fire on every error. Pick whichever ergonomics suit your code.
   *
   * The Loader wraps this callback in its own try/catch — if your handler throws,
   * the Loader keeps working and the secondary failure is logged but not surfaced.
   * The contract guarantees no Pype error ever propagates into the host page's
   * JS runtime.
   *
   * @example
   * ```ts
   * createPypeLoader({
   *   appId: 'app_123',
   *   onError(err) {
   *     if (err.zone === 'loader' && err.code === 'MOUNT_FAILED') {
   *       myTelemetry.report(err);
   *     }
   *   },
   * });
   * ```
   */
  onError?: (err: PypeError) => void;

  /** Language code for the chat interface (e.g. `'en'`, `'es'`). @defaultValue `'en'` */
  language?: AppConfig['language'];
  /** Color theme. Saved design wins; this is the fallback (runtime theme is owned by the saved design, edited in the Pype designer). */
  theme?: AppConfig['theme'];
  /** Launcher button text. Saved design wins; this is the fallback. */
  launcherButtonText?: AppConfig['launcherButtonText'];
  /** Width of the launcher button (CSS value). */
  launcherButtonSize?: AppConfig['launcherButtonSize'];
  /** Position of the launcher on screen (e.g. `'bottom-right'`). */
  launcherPosition?: AppConfig['launcherPosition'];
  /** Visual variant of the launcher button. */
  launcherVariant?: AppConfig['launcherVariant'];
  /** Customer logo URL, shown in the header. Saved design wins; this is the fallback. */
  customerLogoUrl?: AppConfig['customerLogoUrl'];
  /** Customer display name, shown in the header. Saved design wins; this is the fallback. */
  customerName?: AppConfig['customerName'];
  /**
   * Host-supplied user identity (first/last name, email, phone, screen name,
   * token), forwarded to chat-session creation and surfaced to the solution.
   */
  user?: AppConfig['user'];
  /**
   * Opaque passthrough forwarded verbatim to the backend session body — a
   * string, object, or `[{ name, value }]` array. Surfaced to the solution.
   */
  passthrough?: AppConfig['passthrough'];
  /** Host-supplied first message shown while the widget connects. */
  initialMessages?: AppConfig['initialMessages'];
  /** Show the built-in loading placeholder while connecting. @defaultValue `true` */
  showInlineLoader?: AppConfig['showInlineLoader'];
  /** Disable the floating messenger's slide-in/out animation. @defaultValue `false` */
  disableSlideAnimation?: AppConfig['disableSlideAnimation'];
  /** Enable verbose console logging. @defaultValue `false` */
  debug?: AppConfig['debug'];
  /**
   * Render the messenger inside an iframe for CSS/DOM isolation.
   * Config-only — never sourced from the saved design. @defaultValue `false`
   */
  useIframe?: AppConfig['useIframe'];
  /**
   * Rendering modality — same bot flow, different visual presentation.
   * - `'floating'` (default): Launcher FAB + popup messenger.
   * - `'fullpage'`: Messenger fills the container, no launcher.
   *
   * Overrides the saved design's modality; omit to inherit it (which itself
   * falls back to `'floating'`). @defaultValue `'floating'`
   */
  modality?: AppConfig['modality'];
  /**
   * Floating panel frame size. Designer-owned and boot-immutable — pass only
   * for the designer preview (which skips the design fetch); the real widget
   * gets it from the saved design. Ignored in `fullpage` / `launcher:'none'`.
   */
  size?: AppConfig['size'];
  /**
   * What fixed-position chrome anchors to, and what viewport-relative sizing
   * resolves against — `'viewport'` (default, a customer embed) or
   * `'container'`, which keeps the whole widget inside the Loader's `container`
   * and decides the mobile breakpoint from that box instead of the window.
   *
   * `'container'` needs the container to have a definite height. Config-only and
   * boot-immutable — never sourced from the saved design.
   * @defaultValue `'viewport'`
   */
  anchorTo?: AppConfig['anchorTo'];
  /**
   * Floating-modality launcher style. `'none'` disables the built-in
   * launcher so the host page can mount its own.
   * Config-only — never sourced from the saved design. @defaultValue `'default'`
   */
  launcher?: AppConfig['launcher'];
  /**
   * Render target — `'inline'` (default) mounts into the host page;
   * `'window'` opens a same-origin popup and runs the widget inside it (no
   * iframe). Orthogonal to {@link modality}. `boot()` must be called from a
   * user gesture for `'window'`, or the browser blocks the popup. Config-only.
   * @defaultValue `'inline'`
   */
  target?: AppConfig['target'];
  /**
   * Popup tuning for `target: 'window'` (name, size, features, and — for
   * bundler/npm consumers — the runtime `bootstrapUrl`). Ignored for
   * `target: 'inline'`.
   */
  window?: AppConfig['window'];
  /**
   * @internal Pro Studio / internal tooling only — not part of the public
   * embed surface.
   * @defaultValue `false`
   */
  preview?: AppConfig['preview'];
  /**
   * @internal Pro Studio / internal test harnesses only — not part of the
   * public embed surface. Skips the persisted widget-design fetch so
   * caller-supplied config / `applyStyles()` wins.
   * @defaultValue `false`
   */
  skipDesignFetch?: AppConfig['skipDesignFetch'];
  /**
   * @internal Pro Studio / internal tooling only — not part of the public
   * embed surface.
   */
  engagementApiUrl?: AppConfig['engagementApiUrl'];
}

/**
 * Options for {@link PypeLoader.boot}.
 */
export interface BootOptions {
  /**
   * Container to mount `<ps-pype>` into.
   * Overrides the container set in {@link CreatePypeLoaderOptions}.
   * Defaults to `document.body` if neither is provided.
   */
  container?: HTMLElement;
}
